import {
  keepPreviousData,
  QueryKey,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useQuery,
  UseQueryOptions,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import pick from "lodash/pick";
import { useMemo } from "react";

import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import api from "@/common/utils/api";
import { ErrorResponse } from "@reflag/shared/api";
import { UserFilter } from "@reflag/shared/filter";
import {
  UserName,
  UsersBody,
  UsersCountBody,
  UsersCountResponse,
  UserPage,
} from "@reflag/shared/userAPI";

import { commonQueryKeys } from "./commonQueryKeys";

export function useUsers<TData = UserPage>(
  query: Omit<UsersBody, "envId">,
  options?: Omit<
    UseQueryOptions<UserPage, AxiosError<ErrorResponse>, TData>,
    "queryKey" | "queryFn"
  >,
) {
  const { appId, envId } = useEnvironment();

  return useQuery({
    queryKey: commonQueryKeys.users(appId, envId, query),
    queryFn: () =>
      api
        .post<"/apps/:appId/users">(
          `/apps/${appId}/users`,
          { ...query },
          {
            params: { envId },
          },
        )
        .then((res) => res.data),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    placeholderData: keepPreviousData,
    ...options,
    enabled: !!appId && !!envId && options?.enabled !== false,
  });
}

export function useUsersCount(
  query: Omit<UsersCountBody, "envId"> = {},
  options?: Omit<
    UseQueryOptions<UsersCountResponse, AxiosError<ErrorResponse>>,
    "queryKey" | "queryFn"
  >,
) {
  const { appId, envId } = useEnvironment();

  return useQuery({
    queryKey: commonQueryKeys.usersCount(appId, envId, query),
    queryFn: () =>
      api
        .post<"/apps/:appId/envs/:envId/users/count">(
          `/apps/${appId}/envs/${envId}/users/count`,
          { ...query },
        )
        .then((res) => res.data),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    placeholderData: keepPreviousData,
    ...options,
    enabled: !!appId && !!envId && options?.enabled !== false,
  });
}

export function useUsersSearch(
  search?: string,
  query: Omit<UsersBody, "envId" | "idNameFilter"> = {},
  options?: Omit<
    UseInfiniteQueryOptions<
      UserPage,
      AxiosError<ErrorResponse>,
      UserName[],
      QueryKey,
      number
    >,
    "queryKey" | "queryFn" | "initialPageParam" | "getNextPageParam" | "select"
  >,
  envIdOverride?: string,
) {
  const { appId, envId: currentEnvId } = useEnvironment();
  const envId = envIdOverride ?? currentEnvId;

  const body = useMemo<Omit<UsersBody, "envId">>(() => {
    return {
      pageSize: 50,
      idNameEmailFilter: search,
      ...query,
    };
  }, [query, search]);

  return useInfiniteQuery({
    queryKey: commonQueryKeys.users(appId, envId, body),
    queryFn: ({ pageParam = 0 }: { pageParam: number }) =>
      api
        .post<"/apps/:appId/users">(
          `/apps/${appId}/users`,
          {
            ...body,
            pageIndex: pageParam,
          },
          {
            params: { envId: envId! },
          },
        )
        .then((res) => res.data),
    getNextPageParam: (lastPage) => {
      if (lastPage.data.length < lastPage.pageSize) {
        return undefined;
      }

      const nextPage = lastPage.pageIndex + 1;
      return nextPage;
    },
    initialPageParam: 0,
    select: ({ pages }) =>
      pages.flatMap(({ data }) =>
        data.map((item) => pick(item, "id", "name", "email", "avatarUrl")),
      ) ?? [],
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    placeholderData: keepPreviousData,
    ...options,
    enabled: !!appId && !!envId && options?.enabled !== false,
  });
}

export function useUser(userId?: string, envIdOverride?: string) {
  const { appId, envId: currentEnvId } = useEnvironment();
  const envId = envIdOverride ?? currentEnvId;

  return useQuery({
    queryKey: commonQueryKeys.user(appId, envId, userId),
    queryFn: () =>
      api
        .post<"/apps/:appId/users">(
          `/apps/${appId}/users`,
          {
            filter: {
              type: "userAttribute",
              field: "id",
              operator: "IS",
              values: [userId!],
            } satisfies UserFilter,
          },
          {
            params: { envId },
          },
        )
        .then((res) => res.data.data[0]),
    enabled: !!appId && !!envId && !!userId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}
